import { getFontsList, getGoogleFontsUrl, globalFonts } from 'polotno/utils/fonts';

import { fitElementsToPage } from './fitElementsToPage';
import { isSafeZoneElement } from './safeZone';

// Loaded on demand: the importers are megabytes each and most sessions never import.
const loadPdfToJson = async () => (await import('@polotno/pdf-import')).pdfToJson;

const loadSvgToJson = async () => (await import('@polotno/svg-import')).svgToJson;

const JSON_EXTENSIONS = new Set(['json']);
const SVG_EXTENSIONS = new Set(['svg']);
const PDF_EXTENSIONS = new Set(['pdf', 'ai']);

export type AssetUploader = (file: File) => Promise<string>;

/** Renders an SVG data URI to a raster file, or null when it can't. */
export type SvgRasterizer = (svgDataUri: string) => Promise<File | null>;

const SVG_DATA_URI = /^data:image\/svg\+xml[;,]/i;

/**
 * The importer wraps a soft-masked photo in an SVG (the photo and its mask as
 * embedded PNGs) and emits it as an `image` element. That is a photo, not vector
 * art: left inline it is megabytes, and the Design API's save-time SVG→WebP
 * conversion keeps only the mask, so the photo vanishes on reopen
 * (monday 13296564205).
 */
const isSvgWrappedRaster = (element: any): boolean => {
	if (element?.type !== 'image' || typeof element.src !== 'string') return false;
	if (!SVG_DATA_URI.test(element.src)) return false;
	let markup = element.src;
	try {
		markup = decodeURIComponent(markup);
	} catch {
		// Base64 or malformed: fall through on the raw string.
	}
	return /<image\b/i.test(markup) && /data:image\/(png|jpe?g|webp)/i.test(markup);
};

// Rendered at 3× the SVG's own size, longest side capped at 4096px.
const MAX_RASTER_SIDE = 4096;
const RASTER_SCALE = 3;

const loadImage = (src: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = () => reject(new Error('SVG failed to load'));
		img.src = src;
	});

const rasterizeSvgInBrowser: SvgRasterizer = async (svgDataUri) => {
	if (typeof document === 'undefined' || typeof Image === 'undefined') return null;

	try {
		// onload rather than decode() alone: Safari's decode() is unreliable for
		// SVG images, and onload fires after nested data: images are in.
		const img = await loadImage(svgDataUri);
		await img.decode?.().catch(() => undefined);

		const width = img.naturalWidth;
		const height = img.naturalHeight;
		if (!(width > 0 && height > 0)) return null;

		const scale = Math.min(RASTER_SCALE, MAX_RASTER_SIDE / Math.max(width, height));
		const canvas = document.createElement('canvas');
		canvas.width = Math.round(width * scale);
		canvas.height = Math.round(height * scale);
		const context = canvas.getContext('2d');
		if (!context) return null;
		context.drawImage(img, 0, 0, canvas.width, canvas.height);

		const blob: Blob | null = await new Promise((resolve) =>
			canvas.toBlob(resolve, 'image/png'),
		);
		if (!blob) return null;

		const name =
			(await contentHash(blob)) ??
			`import-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
		return new File([blob], `${name}.png`, { type: 'image/png' });
	} catch {
		// A tainted canvas or an SVG the browser can't decode: keep it inline,
		// as before, rather than failing the import.
		return null;
	}
};

/**
 * Rasters and fonts — the two things the importer inlines that carry real
 * weight. Measured on one import, 24 inlined bitmaps were 99.3% of a 31.8 MB
 * design while 16 vector paths were 0.12%, so SVG stays inline (except an
 * `image` element whose SVG only wraps a photo, see isSvgWrappedRaster): uploading it
 * would add a round trip per path for nothing, and an `svg` element's inlined
 * `src` is normal in a design the editor produced itself.
 */
const INLINED_ASSET = /^data:(image\/(png|jpe?g|webp)|font\/[a-z0-9-]+|application\/(x-font-[a-z]+|font-[a-z]+));base64,/;

/**
 * A font style's `src` is a CSS `src` descriptor, not a bare URI — the importer
 * writes a multi-style font as `url("data:font/opentype;base64,…")`.
 */
const CSS_URL = /^url\(\s*(['"]?)(.*?)\1\s*\)$/;

const EXTENSIONS: Record<string, string> = {
	'image/png': 'png',
	'image/jpeg': 'jpg',
	'image/jpg': 'jpg',
	'image/webp': 'webp',
	'font/otf': 'otf',
	'font/opentype': 'otf',
	'font/ttf': 'ttf',
	'font/woff': 'woff',
	'font/woff2': 'woff2',
};

/**
 * Content-addressed name, per the importer author's advice: a PDF repeats the
 * same bitmap on every page, so hashing collapses those to one asset — and the
 * name stays stable across imports rather than being tied to this run.
 */
const contentHash = async (blob: Blob): Promise<string | null> => {
	// `crypto.subtle` exists only in a secure context, so a plain-http dev host
	// that is not localhost has none. Falling back to a random name costs
	// cross-import dedupe, never the upload itself.
	if (!globalThis.crypto?.subtle) return null;

	try {
		const digest = await crypto.subtle.digest(
			'SHA-256',
			await blob.arrayBuffer(),
		);

		return Array.from(new Uint8Array(digest))
			.map((b) => b.toString(16).padStart(2, '0'))
			.join('')
			.slice(0, 32);
	} catch {
		return null;
	}
};

const dataUriToFile = async (dataUri: string): Promise<File> => {
	const blob = await (await fetch(dataUri)).blob();
	const extension = EXTENSIONS[blob.type] || blob.type.split('/')[1] || 'bin';
	const name =
		(await contentHash(blob)) ??
		`import-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

	return new File([blob], `${name}.${extension}`, { type: blob.type });
};

/** Thrown when an asset could not be uploaded, so the caller can say why. */
export class DesignImportAssetError extends Error {
	constructor(cause: unknown) {
		super('An asset from this file could not be uploaded.');
		this.name = 'DesignImportAssetError';
		this.cause = cause;
	}
}

/**
 * Replaces every inlined asset in an imported design with an uploaded URL, so
 * the design the store receives references assets instead of carrying them.
 *
 * Runs before `store.loadJSON`, so an oversized design never reaches the canvas
 * or a save. Covers all four places the importer inlines — element `src`, page
 * backgrounds, and both font shapes — not just element `src`, which leaves a
 * surprising amount behind.
 *
 * A failed upload rejects the whole import. Keeping the rest would hand back a
 * design that still carries inlined assets, which looks fine on the canvas and
 * then fails to save — the user would find out much later, with no way to tell
 * which file caused it. Assets uploaded before the failure stay in the temp
 * session and are swept with any other abandoned upload.
 */
const serializedSize = (json: any) => {
	try {
		return JSON.stringify(json)?.length ?? 0;
	} catch {
		return 0;
	}
};

const asMb = (chars: number) => (chars / 1024 / 1024).toFixed(2);

export const uploadInlinedAssets = async (
	json: any,
	uploadAsset: AssetUploader,
	// Set when only the layers will be kept (an SVG or AI merged into the
	// current page): backgrounds and fonts are discarded, so uploading them would
	// waste a round trip and could fail an import over assets nobody keeps.
	{
		layersOnly = false,
		rasterizeSvg = rasterizeSvgInBrowser,
	}: { layersOnly?: boolean; rasterizeSvg?: SvgRasterizer } = {},
): Promise<any> => {
	const uploaded = new Map<string, string>();

	const replaceSvgWrappedRaster = async (src: string): Promise<string | null> => {
		if (uploaded.has(src)) return uploaded.get(src) as string;

		const file = await rasterizeSvg(src);
		if (!file) {
			// Support needs to see this: the photo stays inline and will not survive save.
			console.warn('Design import: an SVG-wrapped photo could not be rasterized; left inline');
			return null;
		}

		try {
			const url = await uploadAsset(file);
			uploaded.set(src, url);
			return url;
		} catch (error) {
			throw new DesignImportAssetError(error);
		}
	};
	const before = serializedSize(json);

	const replace = async (value: unknown): Promise<string | null> => {
		if (typeof value !== 'string' || !INLINED_ASSET.test(value)) return null;
		if (uploaded.has(value)) return uploaded.get(value) as string;

		try {
			const url = await uploadAsset(await dataUriToFile(value));
			uploaded.set(value, url);
			return url;
		} catch (error) {
			throw new DesignImportAssetError(error);
		}
	};

	const replaceCssSrc = async (value: unknown): Promise<string | null> => {
		const match = typeof value === 'string' ? CSS_URL.exec(value.trim()) : null;
		if (!match) return replace(value);

		const url = await replace(match[2]);
		return url ? `url("${url}")` : null;
	};

	const visit = async (elements: any[]): Promise<void> => {
		for (const element of Array.isArray(elements) ? elements : []) {
			if (element?.type === 'group') {
				await visit(element.children);
				continue;
			}

			const url = isSvgWrappedRaster(element)
				? await replaceSvgWrappedRaster(element.src)
				: await replace(element?.src);
			if (url) element.src = url;
		}
	};

	for (const page of Array.isArray(json?.pages) ? json.pages : []) {
		if (!layersOnly) {
			const background = await replace(page?.background);
			if (background) page.background = background;
		}

		await visit(page?.children);
	}

	const fonts = !layersOnly && Array.isArray(json?.fonts) ? json.fonts : [];

	for (const font of fonts) {
		const url = await replace(font?.url);
		if (url) font.url = url;

		for (const style of Array.isArray(font?.styles) ? font.styles : []) {
			const src = await replaceCssSrc(style?.src);
			if (src) style.src = src;
		}
	}

	// One line, kept deliberately: support needs to be able to see from a console
	// screenshot that the design really did shrink.
	console.info(
		`Design import: ${asMb(before)} MB → ${asMb(serializedSize(json))} MB, ` +
			`${uploaded.size} asset(s) uploaded`,
	);

	return json;
};

const getFileExtension = (fileName = ''): string => {
	const parts = String(fileName).toLowerCase().split('.');
	if (parts.length < 2) {
		return '';
	}

	return parts.pop() || '';
};

const parseJsonFile = async (file: File): Promise<any> => {
	const fileText = await file.text();
	const parsed = JSON.parse(fileText);

	if (parsed?.json && typeof parsed.json === 'object') {
		return parsed.json;
	}

	return parsed;
};

const parseSvgFile = async (file: File): Promise<any> => {
	const svg = await file.text();
	const svgToJson = await loadSvgToJson();
	const bodyChildrenBefore = new Set(Array.from(document.body.children));

	try {
		return await svgToJson({ svg });
	} catch (error) {
		for (const child of Array.from(document.body.children)) {
			if (!bodyChildrenBefore.has(child)) {
				child.remove();
			}
		}

		throw error;
	}
};

const parsePdfOrAiFile = async (file: File): Promise<any> => {
	const pdf = await file.arrayBuffer();
	const pdfToJson = await loadPdfToJson();

	return pdfToJson({ pdf });
};

// The imported document's own page box: the page's size when the importer set
// one (PDF/AI), else the design size (SVG viewBox; the importer already shifts
// children by the viewBox origin, so the box starts at 0,0). Null lets the fit
// measure the elements instead.
export const importedPageBounds = (page: any, json: any) => {
	const width = Number(page?.width) || Number(json?.width);
	const height = Number(page?.height) || Number(json?.height);

	return width > 0 && height > 0 ? { x: 0, y: 0, width, height } : null;
};

// Hex, rgb()/hsl(), a CSS colour name, or a gradient. Anything else in
// `background` (absolute URL, data URI, relative path) is an image.
const COLOR_BACKGROUND =
	/^(#[0-9a-f]{3,8}|(rgb|hsl)a?\(.*\)|[a-z]+|(linear|radial|conic)-gradient\(.*\))$/i;

// A page with no elements and at most a colour background. An image background
// is artwork — a page carrying one is content, not a slot. The editor-only
// safe-zone overlay (Instagram portrait) is chrome, not content (monday 13296564205).
export const isEmptyPage = (page: any): boolean => {
	const hasChildren =
		Array.isArray(page?.children) &&
		page.children.some((child: any) => !isSafeZoneElement(child));
	const background =
		typeof page?.background === 'string' ? page.background.trim() : '';

	return !hasChildren && (background === '' || COLOR_BACKGROUND.test(background));
};

const mergeImportedLayersIntoCurrentPage = (
	store: any,
	importedJson: any,
): void => {
	const importedPages = Array.isArray(importedJson?.pages)
		? importedJson.pages
		: [];

	const pagesWithChildren = importedPages.filter(
		(page) => Array.isArray(page?.children) && page.children.length > 0,
	);

	if (pagesWithChildren.length === 0) {
		return;
	}

	const targetPage = store.activePage || store.pages?.[0];

	if (!targetPage) {
		store.loadJSON(importedJson);
		return;
	}

	// Prefer the page's own numeric size, else the store's (Polotno pages may be
	// 'auto' and inherit the store size).
	const pageWidth = Number(targetPage.computedWidth) || Number(store.width);
	const pageHeight = Number(targetPage.computedHeight) || Number(store.height);

	// Each imported page is fitted on its own, against its own document size
	// (SVG viewBox or PDF/AI artboard), so the artwork keeps its layout and
	// whitespace. Only ever shrink: a small logo merged into a large design must
	// not be blown up over the existing content.
	for (const page of pagesWithChildren) {
		const fitted = fitElementsToPage(page.children, {
			pageWidth,
			pageHeight,
			bounds: importedPageBounds(page, importedJson),
			maxScale: 1,
		});

		for (const element of fitted) {
			targetPage.addElement(element);
		}
	}
};

export type PagesSkippedHandler = (skipped: number, total: number) => void;

// Keeps the first `pageLimit` pages; runs before upload so dropped pages' assets aren't sent.
const limitPages = (
	json: any,
	pageLimit?: number,
	onPagesSkipped?: PagesSkippedHandler,
): any => {
	const pages = Array.isArray(json?.pages) ? json.pages : [];

	if (pageLimit === undefined || pages.length <= pageLimit) {
		return json;
	}

	onPagesSkipped?.(pages.length - pageLimit, pages.length);
	return { ...json, pages: pages.slice(0, pageLimit) };
};

const mergeFonts = (
	currentFonts: any[] = [],
	importedFonts: any[] = [],
): any[] => {
	const fontMap = new Map<string, any>();

	for (const font of [...currentFonts, ...importedFonts]) {
		if (!font || !font.fontFamily) {
			continue;
		}

		fontMap.set(font.fontFamily, font);
	}

	return Array.from(fontMap.values());
};

export const convertDesignFileToJson = async (
	file: File,
	{
		uploadAsset,
		layersOnly = false,
		pageLimit,
		onPagesSkipped,
	}: {
		uploadAsset?: AssetUploader;
		layersOnly?: boolean;
		// Max pages to keep from a PDF/AI; absent keeps them all.
		pageLimit?: number;
		onPagesSkipped?: PagesSkippedHandler;
	} = {},
): Promise<any> => {
	const extension = getFileExtension(file?.name);
	const mimeType = String(file?.type || '').toLowerCase();

	if (JSON_EXTENSIONS.has(extension) || mimeType.includes('json')) {
		return parseJsonFile(file);
	}

	const withUploadedAssets = async (json: any) =>
		uploadAsset
			? uploadInlinedAssets(json, uploadAsset, { layersOnly })
			: json;

	if (SVG_EXTENSIONS.has(extension) || mimeType.includes('svg')) {
		return withUploadedAssets(await parseSvgFile(file));
	}

	if (
		PDF_EXTENSIONS.has(extension) ||
		mimeType.includes('pdf') ||
		mimeType.includes('illustrator')
	) {
		return withUploadedAssets(
			limitPages(
				await parsePdfOrAiFile(file),
				pageLimit,
				onPagesSkipped,
			),
		);
	}

	throw new Error(
		'Unsupported design file type. Please use JSON, SVG, PDF, or AI.',
	);
};

const STYLE_WORDS =
	'thin|hairline|extra ?light|ultra ?light|light|regular|normal|book|medium|semi ?bold|demi ?bold|bold|extra ?bold|ultra ?bold|black|heavy|italic|oblique';
const SPACE_SUFFIX = new RegExp(`\\s+(?:${STYLE_WORDS})$`, 'i');

/**
 * `Figtree-Black` -> `Figtree`. SVG exporters write the face name into
 * `font-family`; the loadable family is what precedes the last `-` (or a
 * trailing style word). Returns the input when there is nothing to strip.
 */
const stripFaceSuffix = (family: string): string => {
	const dash = family.lastIndexOf('-');
	if (dash > 0) return family.slice(0, dash).trim() || family;
	return family.replace(SPACE_SUFFIX, '').trim() || family;
};

// `@polotno/svg-import` copies `font-family` verbatim, so it may arrive quoted
// or as a comma list (`'Figtree-Black', sans-serif`); keep only the first family.
const normalizeFamily = (family: string): string =>
	family.split(',')[0].trim().replace(/^['"]|['"]$/g, '').trim();

// What the server raster can load is a Google Fonts family (or a store font,
// handled as `known`). Ask Google directly: its CSS endpoint answers 400 for
// an unknown family. Probing through the browser's own font loading would
// also pass fonts merely installed on the user's machine, which the server
// then times out on — the same 500 this guards against.
const isGoogleFamily = async (family: string): Promise<boolean> => {
	try {
		const response = await fetch(getGoogleFontsUrl(family), { method: 'GET' });
		return response.ok;
	} catch {
		return false;
	}
};

const collectTextElements = (children: any[] | undefined, out: any[]) => {
	for (const child of Array.isArray(children) ? children : []) {
		if (child?.type === 'text') out.push(child);
		else if (child?.type === 'group') collectTextElements(child.children, out);
	}
	return out;
};

/**
 * `@polotno/svg-import` copies the SVG's `font-family` verbatim, and exporters
 * write a face name (`Figtree-Black`) there. Polotno cannot load that: the font
 * load times out, and the server-side raster then fails the whole save. Rewrite
 * each unloadable family to its base family, or to a fallback, before the
 * elements reach the store. Mutates `json` in place. The weight is left as the
 * importer set it — it already encodes the face.
 */
export const resolveImportedFontFamilies = async (
	json: any,
	store: any,
	options: {
		isLoadable?: (family: string) => Promise<boolean>;
		fallbackFamily?: string;
	} = {},
): Promise<void> => {
	const elements: any[] = [];
	for (const page of Array.isArray(json?.pages) ? json.pages : []) {
		collectTextElements(page?.children, elements);
	}
	if (!elements.length) return;

	const known = new Set<string>();
	const addKnown = (fonts: any) => {
		for (const font of Array.isArray(fonts) ? fonts : []) {
			if (font?.fontFamily) known.add(font.fontFamily);
		}
	};
	addKnown(store?.toJSON?.()?.fonts);
	addKnown(globalFonts);
	for (const family of getFontsList()) known.add(family);

	const isLoadable = options.isLoadable ?? isGoogleFamily;
	const fallback = options.fallbackFamily ?? getFontsList()[0] ?? 'Roboto';

	const probes = new Map<string, boolean>();
	const loads = async (family: string) => {
		if (known.has(family)) return true;
		if (!probes.has(family)) probes.set(family, await isLoadable(family));
		return probes.get(family) as boolean;
	};

	const resolved = new Map<string, string>();
	for (const element of elements) {
		if (typeof element.fontFamily !== 'string') continue;
		const family = normalizeFamily(element.fontFamily);
		if (!family) continue;
		if (!resolved.has(family)) {
			const base = stripFaceSuffix(family);
			if (await loads(family)) resolved.set(family, family);
			else if (base !== family && (await loads(base))) resolved.set(family, base);
			else resolved.set(family, fallback);
		}
		element.fontFamily = resolved.get(family);
	}
};

export const loadDesignFileToStore = async ({
	file,
	store,
	// Absent, the design keeps its inlined rasters — previous behaviour.
	uploadAsset,
	// The design's page cap; an appended PDF stops at it. Absent, no limit — previous behaviour.
	maxPages,
	onPagesSkipped,
}: {
	file: File;
	store: any;
	uploadAsset?: AssetUploader;
	maxPages?: number;
	onPagesSkipped?: PagesSkippedHandler;
}): Promise<any> => {
	const extension = getFileExtension(file?.name);
	const mergesLayersOnly = extension === 'svg' || extension === 'ai';
	// Only a PDF adds pages (SVG/AI merge into the current page)
	const appendsPages = PDF_EXTENSIONS.has(extension) && !mergesLayersOnly;
	// Empty existing pages are replaced by the import rather than kept, so only
	// a design with content uses up part of the page cap.
	const pagesBefore: any[] = appendsPages ? store.toJSON?.()?.pages || [] : [];
	const keptPageCount = pagesBefore.every(isEmptyPage) ? 0 : pagesBefore.length;
	const json = await convertDesignFileToJson(file, {
		uploadAsset,
		// With no page to merge into, the whole design is loaded instead, so its
		// backgrounds and fonts are kept and must be uploaded too.
		layersOnly:
			mergesLayersOnly && Boolean(store.activePage || store.pages?.[0]),
		pageLimit:
			appendsPages && maxPages
				? Math.max(maxPages - keptPageCount, 0)
				: undefined,
		onPagesSkipped,
	});

	if (mergesLayersOnly) {
		// SVG text carries a face name (`Figtree-Black`) as its family, which
		// Polotno cannot load and the server raster then fails on; fix it before
		// the merge copies the elements.
		if (extension === 'svg') await resolveImportedFontFamilies(json, store);
		mergeImportedLayersIntoCurrentPage(store, json);
		return json;
	}

	if (appendsPages) {
		const existingJson = store.toJSON();
		const existingPages: any[] = existingJson.pages || [];
		// A fresh design starts with one empty page; appending after it leaves the
		// artwork on page 2 and a blank page 1 (blank thumbnail). When nothing is
		// on the existing pages, the imported pages take their slots — keeping each
		// slot's own size and preset metadata (`custom`), fitted to that slot.
		// Positional, not wholesale: a preset's extra pages (minPages, per-format
		// pages) stay.
		const existingIsEmpty = existingPages.every(isEmptyPage);
		// Every page is fitted against its own source page box, so a page with a
		// single small element keeps its layout and all pages of one file share a
		// scale.
		const fitIntoSlot = (page: any, slot?: any) => {
			const width = Number(slot?.width) || Number(store.width);
			const height = Number(slot?.height) || Number(store.height);

			// The slot's safe-zone overlay stays on top of the imported artwork.
			const overlay = Array.isArray(slot?.children)
				? slot.children.filter(isSafeZoneElement)
				: [];
			// A filled slot keeps its own id: listing-overlay previews are keyed on
			// the preset page ids. Written back onto the importer page too, so a
			// caller selecting the first imported page by `json.pages[0].id` lands on it.
			if (slot?.id && page) page.id = slot.id;
			// The slot's preset metadata (format, network) decides whether the
			// safe-zone overlay belongs on the page, so the importer must not replace it.
			const custom = slot?.custom ?? page?.custom;

			if (!(width > 0 && height > 0)) {
				if (!slot) return page;
				return {
					...slot,
					...page,
					custom,
					children: [...(page?.children || []), ...overlay],
				};
			}

			return {
				...slot,
				...page,
				custom,
				width,
				height,
				children: [
					...fitElementsToPage(page?.children, {
						pageWidth: width,
						pageHeight: height,
						bounds: importedPageBounds(page, json),
					}),
					...overlay,
				],
			};
		};
		const importedPages = (Array.isArray(json?.pages) ? json.pages : []).map(
			(page: any, index: number) =>
				fitIntoSlot(page, existingIsEmpty ? existingPages[index] : undefined),
		);
		store.loadJSON({
			...existingJson,
			fonts: mergeFonts(existingJson.fonts, json.fonts),
			pages: existingIsEmpty
				? [...importedPages, ...existingPages.slice(importedPages.length)]
				: [...existingPages, ...importedPages],
		});
		return json;
	}

	store.loadJSON(json);
	return json;
};
